import { IPayPeriod, TPayPeriodPart } from "@/Common/api/types/CommissionCycle";
import { matchPositions, positionStatusMapper } from "@/Common/helpers/Positions";
import { ICombinedPositionStatuses } from "@/Washouts/types/PayrollClosing/payrollSubmission";
import { Checkbox, FormControlLabel, Grid, Paper, Radio, RadioGroup, Tooltip, Typography } from "@mui/material";
import clsx from "clsx";

type TSubmissionStatusesProps = {
	partnerPayPeriod?: IPayPeriod | null;
	allowedStatuses: string[];
	payPeriodParts?: TPayPeriodPart[];
	editPayroll: boolean;
	isFinalizedCC: boolean;
	isCompletedPP: boolean;
	combinedPositionStatuses: ICombinedPositionStatuses[];
	selectedPayPeriodPart: string;
	selectedPositions: Record<string, string[]>;
	setSelectedPayPeriodPart: React.Dispatch<React.SetStateAction<string>>;
	setSelectedPositions: React.Dispatch<
		React.SetStateAction<{
			[x: string]: string[];
		}>
	>;
};

const checkPerformanceBaseDraw = (combinedPositionStatuses: ICombinedPositionStatuses[], position: string) => {
	const isReviewableInBothParts =
		combinedPositionStatuses.filter((item) => item.position === position && item.status !== "UNREVIEWABLE").length > 1;
	const isNotPostedInPartOne = !combinedPositionStatuses.find(
		(item) => item.position === position && item.status === "POSTED_PAYROLL" && item.part === 1
	);
	return { isReviewableInBothParts, isNotPostedInPartOne };
};

const checkSelectedInContraryPart = ({
	combinedPositionStatuses,
	commissionCycleId,
	selectedPositions,
	position,
}: {
	combinedPositionStatuses: ICombinedPositionStatuses[];
	selectedPositions: Record<string, string[]>;
	position: string;
	commissionCycleId?: string;
}) => {
	const contraryCcId = combinedPositionStatuses.find((item) => item.commissionCycleId !== commissionCycleId)?.commissionCycleId ?? "";
	return !!selectedPositions[contraryCcId]?.includes(position);
};

const resolveTooltipTitle = ({
	isSecondPartPendingPosted,
	isSelectedInOppositePart,
}: {
	isSecondPartPendingPosted: boolean;
	isSelectedInOppositePart: boolean;
}) => {
	if (isSecondPartPendingPosted) {
		return "Partial pay periods must be submitted in sequential order. Please submit the first part before submitting the second part.";
	}
	if (isSelectedInOppositePart) {
		return "Partial pay periods must be submitted one at a time.";
	}
	return "";
};

const SubmissionStatuses = (props: TSubmissionStatusesProps) => {
	const {
		partnerPayPeriod,
		allowedStatuses,
		payPeriodParts,
		editPayroll,
		isFinalizedCC,
		isCompletedPP,
		selectedPayPeriodPart,
		selectedPositions,
		setSelectedPayPeriodPart,
		setSelectedPositions,
		combinedPositionStatuses,
	} = props;
	const generalDisabled = isFinalizedCC || !editPayroll || isCompletedPP;

	const handleSelectedPayPeriodPartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedPayPeriodPart(event.target.value);
	};

	const handleSelectPosition = (position: string) => {
		setSelectedPositions((prev) => {
			const current = prev[selectedPayPeriodPart] ?? [];
			let updated: string[];
			if (current.includes(position)) {
				updated = current.filter((p) => p !== position);
			} else {
				updated = [...current, position];
			}
			return { ...prev, [selectedPayPeriodPart]: updated };
		});
	};

	const handleAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedPositions((prev) => ({
			...prev,
			[selectedPayPeriodPart]: event.target.checked ? selectablePositions : [],
		}));
	};

	const filteredPositionStatuses = combinedPositionStatuses.filter((item) => item.commissionCycleId === selectedPayPeriodPart);
	const selectablePositions = filteredPositionStatuses
		.filter(({ part, position, status }) => {
			const { isReviewableInBothParts, isNotPostedInPartOne } = checkPerformanceBaseDraw(combinedPositionStatuses, position);
			const disabled =
				generalDisabled || !allowedStatuses.includes(status) || (isReviewableInBothParts && isNotPostedInPartOne && part === 2);
			return !disabled;
		})
		.map(({ position }) => position);

	const allSelected =
		selectablePositions.length > 0 &&
		selectablePositions.every((position) => selectedPositions[selectedPayPeriodPart]?.includes(position));

	const someSelected =
		selectablePositions.some((position) => selectedPositions[selectedPayPeriodPart]?.includes(position)) && !allSelected;

	return (
		<Grid container direction={"column"}>
			{!!partnerPayPeriod && (
				<Grid item>
					<RadioGroup
						row
						aria-labelledby="radio-buttons-group-export-process-label"
						name="radio-buttons-group-export-process"
						className="gap-2"
						value={selectedPayPeriodPart}
						onChange={handleSelectedPayPeriodPartChange}
					>
						{payPeriodParts?.map(({ label, ccId }) => {
							return (
								<FormControlLabel
									key={ccId}
									value={ccId}
									control={
										<Radio
											size="small"
											disabled={generalDisabled}
											disableFocusRipple
											disableRipple
											className={clsx({
												"[&_.MuiSvgIcon-root]:!text-[16px]": true,
												"[&.Mui-checked]:!text-primary  !text-primary": !generalDisabled,
												"!text-disabled": generalDisabled,
											})}
										/>
									}
									label={
										<Typography
											className={clsx({
												"!font-sans !text-[14.5px] !font-normal": true,
												"!text-default": !generalDisabled,
												"!text-disabled": generalDisabled,
											})}
										>
											{label}
										</Typography>
									}
								/>
							);
						})}
					</RadioGroup>
				</Grid>
			)}
			<Grid item sx={{ paddingTop: "10px" }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={allSelected}
							indeterminate={someSelected}
							disabled={selectablePositions.length === 0}
							onChange={handleAllChange}
						/>
					}
					label="Select All"
					labelPlacement="end"
					className="group-pay-plan-assign-container-select-all"
				/>
			</Grid>
			<Grid item sx={{ paddingTop: "10px", paddingBottom: "20px" }}>
				<Grid container columnSpacing={18}>
					{filteredPositionStatuses.map(({ commissionCycleId, part, position, status }, index) => {
						const key = `${index}-${commissionCycleId}-${part}-${position}`;
						const { isReviewableInBothParts, isNotPostedInPartOne } = checkPerformanceBaseDraw(
							combinedPositionStatuses,
							position
						);
						const isNotReviewable = !allowedStatuses.includes(status);
						const isSecondPartPendingPosted = isReviewableInBothParts && part === 2 && isNotPostedInPartOne;
						const isSelectedInOppositePart =
							isReviewableInBothParts &&
							checkSelectedInContraryPart({ combinedPositionStatuses, commissionCycleId, selectedPositions, position });

						const disabled = generalDisabled || isNotReviewable || isSecondPartPendingPosted || isSelectedInOppositePart;

						const tooltipTitle = resolveTooltipTitle({
							isSecondPartPendingPosted,
							isSelectedInOppositePart,
						});

						const disableTooltipTitle = !disabled || !tooltipTitle;

						const selected = isReviewableInBothParts
							? selectedPositions[selectedPayPeriodPart]?.includes(position)
							: Object.keys(selectedPositions).some((key) => selectedPositions[key]?.includes(position));

						return (
							<Grid item key={key} xs={4}>
								<Grid container direction={"row"} justifyContent={"space-between"}>
									<Grid item>
										<FormControlLabel
											control={
												<Checkbox
													disabled={disabled}
													checked={selected}
													onChange={() => {
														handleSelectPosition(position);
													}}
												/>
											}
											label={
												<Tooltip
													title={<Typography className="!font-sans !text-sm">{tooltipTitle}</Typography>}
													arrow
													placement="top"
													disableHoverListener={disableTooltipTitle}
													disableFocusListener={disableTooltipTitle}
												>
													<span>{matchPositions(position)}</span>
												</Tooltip>
											}
											labelPlacement="end"
											className="payroll-submission-position"
										/>
									</Grid>{" "}
									<Grid item className={`payroll-submission-position-status`}>
										{status !== "PENDING" && (
											<Paper className="!rounded-[6px] !border-primary" elevation={0}>
												<Typography component="div" className="!text-primary">
													{positionStatusMapper(status ?? "")}
												</Typography>
											</Paper>
										)}
									</Grid>
								</Grid>
							</Grid>
						);
					})}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default SubmissionStatuses;
